import { getApiTable } from "./qmsApiUtils";

export const VAULT_AUDIT_LOG_CATEGORIES = [
  { id: "all", label: "All" },
  { id: "workflow", label: "Review & approval" },
  { id: "checkout", label: "Check-in / out" },
  { id: "comments", label: "Comments" },
  { id: "quiz", label: "Quiz" },
  { id: "library", label: "Document library" },
  { id: "learning", label: "My learning" },
  { id: "content", label: "Content & versions" },
  { id: "publish", label: "Publish & archive" },
  { id: "other", label: "Other" },
];

const CATEGORY_META = {
  workflow: { icon: "fas fa-project-diagram", tone: "workflow" },
  checkout: { icon: "fas fa-lock", tone: "checkout" },
  comments: { icon: "fas fa-comment-dots", tone: "comments" },
  quiz: { icon: "fas fa-question-circle", tone: "quiz" },
  library: { icon: "fas fa-book", tone: "library" },
  learning: { icon: "fas fa-graduation-cap", tone: "learning" },
  content: { icon: "fas fa-file-alt", tone: "content" },
  publish: { icon: "fas fa-bullhorn", tone: "publish" },
  other: { icon: "fas fa-clipboard-list", tone: "other" },
};

function readRowValue(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
}

export function getVaultAuditLogCategory(actionName) {
  const action = String(actionName || "").trim().toLowerCase();
  if (!action) return "other";

  if (
    action.includes("reviewer") ||
    action.includes("review") ||
    action.includes("approver") ||
    action.includes("approval") ||
    action.includes("document from reviewer") ||
    action.includes("document from approver")
  ) {
    return "workflow";
  }
  if (
    action.includes("checked out") ||
    action.includes("checked in") ||
    action.includes("checkin") ||
    action.includes("check in") ||
    action.includes("check-out") ||
    action.includes("check out") ||
    action.includes("checkout") ||
    action.includes("cancel checkout") ||
    action.includes("check out cancelled") ||
    action.includes("checkout cancelled")
  ) {
    return "checkout";
  }
  if (action.includes("quiz") || action.includes("question")) return "quiz";
  if (action.includes("document library") || action.includes("library")) return "library";
  if (action.includes("my learning") || action.includes("reading list")) return "learning";
  if (action.includes("comment")) return "comments";
  if (
    action.includes("associated") ||
    action.includes("version") ||
    action.includes("upload") ||
    action.includes("document saved") ||
    action.includes("document updated")
  ) {
    return "content";
  }
  if (
    action.includes("publish") ||
    action.includes("archiv") ||
    action.includes("transfer") ||
    action.includes("superseded")
  ) {
    return "publish";
  }
  return "other";
}

export function getVaultAuditLogCategoryMeta(categoryId) {
  return CATEGORY_META[categoryId] || CATEGORY_META.other;
}

const VAULT_AUDIT_ACTION_LABELS = {
  "document checked in": "Checked in",
  "document checked out": "Checked out",
  "check out cancelled": "Checkout cancelled",
  "document commented": "Comment added",
  "document comment edited": "Comment edited",
  "document comment deleted": "Comment deleted",
  "document reviewer saved": "Reviewer added",
  "document reviewer removed": "Reviewer removed",
  "document approver saved": "Approver added",
  "document approver removed": "Approver removed",
  "document sent for review": "Sent for review",
  "document sent for approval": "Sent for approval",
  "review completed": "Review completed",
  "approval completed": "Approval completed",
  "document rejected": "Document rejected",
  "approval rejected": "Approval rejected",
  "associated documents saved": "Links saved",
  "associated documents removed": "Links removed",
  "document title updated": "Title updated",
  "document created": "Created",
  "document published": "Published",
  "document publish authorized": "Publish authorized",
  "document owner transferred": "Owner transferred",
  "document archived": "Archived",
  "document unarchived": "Unarchived",
  "document from reviewer": "Returned from review",
  "document from approver": "Returned from approval",
};

export function formatVaultAuditActionLabel(actionName) {
  const raw = String(actionName || "").trim();
  if (!raw) return "—";

  const mapped = VAULT_AUDIT_ACTION_LABELS[raw.toLowerCase()];
  if (mapped) return mapped;

  const withoutDocumentPrefix = raw.replace(/^document\s+/i, "");
  if (withoutDocumentPrefix !== raw) {
    return withoutDocumentPrefix.charAt(0).toUpperCase() + withoutDocumentPrefix.slice(1);
  }

  return raw;
}

export function parseVaultAuditLogJsonDetails(jsonData) {
  const raw = String(jsonData ?? "").trim();
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed?.previousOwner || parsed?.newOwner) {
      return {
        type: "ownershipTransfer",
        module: parsed.module || "",
        action: parsed.action || "",
        previousOwner: parsed.previousOwner || null,
        newOwner: parsed.newOwner || null,
      };
    }
    if (Array.isArray(parsed?.staff)) {
      return {
        type: "staffList",
        module: parsed.module || "",
        action: parsed.action || "",
        versionNumber: parsed.versionNumber ?? "",
        staff: parsed.staff,
      };
    }
    if (parsed && typeof parsed === "object") {
      return { type: "object", data: parsed };
    }
  } catch (_e) {
    return { type: "text", text: raw };
  }

  return { type: "text", text: raw };
}

function parseAuditDateSortValue(row) {
  const raw = readRowValue(row, "ActionDateTimeRaw", "actionDateTimeRaw");
  if (raw) {
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) return parsed;
  }

  const formatted = readRowValue(row, "ActionDateTime", "actionDateTime");
  if (formatted) {
    const parsed = Date.parse(formatted.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3"));
    if (!Number.isNaN(parsed)) return parsed;
  }

  return 0;
}

export function normalizeVaultAuditLogRow(row, index = 0) {
  const actionName = readRowValue(row, "ActionName", "actionName");
  const comments = readRowValue(row, "Comments", "comments");
  const category = getVaultAuditLogCategory(actionName);
  const jsonDetails = parseVaultAuditLogJsonDetails(
    readRowValue(row, "JsonData", "jsonData"),
  );

  return {
    id:
      readRowValue(row, "GUIDPK", "guidpk", "GuidPk") ||
      `${actionName}-${readRowValue(row, "ActionDateTime", "actionDateTime")}-${index}`,
    actionName,
    actionLabel: formatVaultAuditActionLabel(actionName),
    actionBy: readRowValue(row, "ActionBy", "actionBy") || "System",
    actionDateTime: readRowValue(row, "ActionDateTime", "actionDateTime") || "—",
    actionDateTimeRaw: readRowValue(row, "ActionDateTimeRaw", "actionDateTimeRaw"),
    comments: comments || "N/A",
    hasComments: Boolean(comments),
    jsonDetails,
    hasDetails: Boolean(jsonDetails),
    category,
    categoryMeta: getVaultAuditLogCategoryMeta(category),
    sortValue: parseAuditDateSortValue(row),
  };
}

export function getVaultAuditLogEntries(response) {
  const rows = getApiTable(response, "DocumentLog");
  return (Array.isArray(rows) ? rows : [])
    .map((row, index) => normalizeVaultAuditLogRow(row, index))
    .sort((a, b) => b.sortValue - a.sortValue || String(b.id).localeCompare(String(a.id)));
}

export function filterVaultAuditLogEntries(entries, { search = "", category = "all" } = {}) {
  const query = String(search || "").trim().toLowerCase();
  return (entries || []).filter((entry) => {
    if (category !== "all" && entry.category !== category) return false;
    if (!query) return true;

    const haystack = [
      entry.actionName,
      entry.actionLabel,
      entry.actionBy,
      entry.actionDateTime,
      entry.comments,
      entry.jsonDetails?.module,
      entry.jsonDetails?.action,
      ...(entry.jsonDetails?.staff || []).map((person) => person.staffName || person.emailId),
    ]
      .join(" ")
      .toLowerCase();

    return haystack.includes(query);
  });
}

export function buildVaultAuditLogSummary(entries) {
  const list = Array.isArray(entries) ? entries : [];
  const categoryCounts = VAULT_AUDIT_LOG_CATEGORIES.reduce((acc, item) => {
    if (item.id !== "all") acc[item.id] = 0;
    return acc;
  }, {});

  list.forEach((entry) => {
    categoryCounts[entry.category] = (categoryCounts[entry.category] || 0) + 1;
  });

  return {
    total: list.length,
    lastActivity: list[0]?.actionDateTime || "—",
    categoryCounts,
  };
}

export function exportVaultAuditLogCsv(entries) {
  const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const header = [
    "Action Name",
    "Action Done By",
    "Category",
    "Action Date & Time",
    "Comments",
  ];
  const lines = (entries || []).map((entry) =>
    [
      entry.actionLabel || entry.actionName,
      entry.actionBy || "—",
      VAULT_AUDIT_LOG_CATEGORIES.find((item) => item.id === entry.category)?.label ||
        entry.category,
      entry.actionDateTime,
      entry.hasComments ? entry.comments : "N/A",
    ]
      .map(escape)
      .join(","),
  );

  return [header.map(escape).join(","), ...lines].join("\r\n");
}
